import { paths, type Service, type ServiceQuestion } from '@acs/shared';
import { collection, deleteDoc, deleteField, doc, onSnapshot, orderBy, query, serverTimestamp, setDoc } from 'firebase/firestore';
import { serviceConverter, type ServiceDoc } from './converters.js';
import { db } from './firebase.js';

export function subscribeServices(businessId: string, cb: (rows: ServiceDoc[]) => void): () => void {
  const q = query(
    collection(db, paths.services(businessId)).withConverter(serviceConverter),
    orderBy('sortOrder', 'asc'),
  );
  return onSnapshot(q, (snap) => cb(snap.docs.map((d) => d.data())));
}

/** Create or replace a service. Pass an id to update, omit to create. */
export async function upsertService(
  businessId: string,
  service: Service,
  serviceId?: string,
): Promise<void> {
  const ref = serviceId
    ? doc(db, paths.service(businessId, serviceId))
    : doc(collection(db, paths.services(businessId)));
  await setDoc(ref, service, { merge: true });
}

export async function deleteService(businessId: string, serviceId: string): Promise<void> {
  await deleteDoc(doc(db, paths.service(businessId, serviceId)));
}

/** What the owner edits on an existing service. `priceMax: null` = a fixed price. */
export interface ServiceEdit {
  name: string;
  durationMin: number;
  allDay: boolean;
  price: number;
  priceMax: number | null;
  /** Asked before booking, in order (doc 64). `[]` = none — written as such. */
  questions: ServiceQuestion[];
}

/**
 * Change a service's name, length, price and questions (doc 45 allowed the
 * name only). Bookings already made keep what they were booked under — they
 * are a record of what was booked, answers included (doc 64) — and new ones
 * take the new values. Merged, so `active` and `sortOrder` are untouched; a
 * cleared range removes `priceMax` rather than leaving a stale upper bound
 * behind, and `questions` is always written — an empty list included, or a
 * merge would keep the questions the owner just removed.
 */
export async function updateService(businessId: string, serviceId: string, edit: ServiceEdit): Promise<void> {
  const { priceMax, ...rest } = edit;
  await setDoc(
    doc(db, paths.service(businessId, serviceId)),
    { ...rest, priceMax: priceMax ?? deleteField(), updatedAt: serverTimestamp() },
    { merge: true },
  );
}
